import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  Button,
  Alert,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  Chip,
} from '@mui/material';

interface Option {
  id: string;
  name: string;
}

interface FieldConfig {
  field: string;
  label: string;
  targetApiPath: string;
  needsBusiness: boolean;
}

// Only fields with their OWN pre-settable Matched<X>Id column on
// ImportedRow are offered — see
// ImportedRowMatchingService.AutoFillFields for the full rationale.
// Product/Category/GeneStack are deliberately excluded: Product must never
// be bulk-guessed (a blank Product means a genuinely different, unknown
// product per row), and Category/GeneStack have no independent per-row
// slot to pre-set at all.
const FIELD_CONFIGS: FieldConfig[] = [
  { field: 'UnitName', label: 'Unit', targetApiPath: '/api/units', needsBusiness: false },
  { field: 'OperationType', label: 'Operation Type', targetApiPath: '/api/operation-types', needsBusiness: false },
  { field: 'RegionName', label: 'Region', targetApiPath: '/api/regions', needsBusiness: false },
  { field: 'BusinessName', label: 'Business', targetApiPath: '/api/businesses/list', needsBusiness: false },
  { field: 'FarmName', label: 'Farm', targetApiPath: '/api/spatial-features?category=Farm', needsBusiness: true },
  { field: 'FieldName', label: 'Field', targetApiPath: '/api/spatial-features?category=Field', needsBusiness: true },
];

export interface ActiveFilter {
  column: string;
  label: string;
  value: string;
}

// 'filtered': every unmatched row matching the grid's filters, replacing
// what it has. 'blank': every unmatched row in the batch with nothing for
// the field.
type Scope = 'filtered' | 'blank';

interface Props {
  batchId: string;
  // The review grid's current filters — what 'filtered' scope applies to.
  filters: ActiveFilter[];
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}

// "Auto Fill" — pick a field (e.g. Unit) and a value (e.g. Litres/ha), and
// either every row matching the grid's current filters gets it (a bulk
// correction: Kg/ha on every row of one product), or every still-unmatched
// row in this batch with NOTHING at all for that field does — the bulk fix
// for source formats (Agworld) that never state a per-row unit at all, so
// hundreds/thousands of otherwise-identical blanks don't each need a trip
// through the wizard.
const ImportAutoFillDialog: React.FC<Props> = ({ batchId, filters, open, onClose, onApplied }) => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [fieldKey, setFieldKey] = useState(FIELD_CONFIGS[0].field);
  const config = FIELD_CONFIGS.find((c) => c.field === fieldKey)!;
  const [scope, setScope] = useState<Scope>('blank');

  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [targetOptions, setTargetOptions] = useState<Option[]>([]);
  const [targetId, setTargetId] = useState<string | null>(null);

  const [dryRunCount, setDryRunCount] = useState<number | null>(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [appliedCount, setAppliedCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    setFieldKey(FIELD_CONFIGS[0].field);
    setScope(filters.length > 0 ? 'filtered' : 'blank');
    setSelectedBusinessId(null);
    setTargetId(null);
    setDryRunCount(null);
    setAppliedCount(null);
    setError(null);
    (async () => {
      const res = await fetch('/api/businesses/list', { headers: authHeaders() });
      if (res.ok) setBusinesses((await res.json()).map((b: any) => ({ id: b.id, name: b.name })));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset the downstream picks whenever the field itself changes.
  useEffect(() => {
    setSelectedBusinessId(null);
    setTargetId(null);
    setDryRunCount(null);
    setAppliedCount(null);
  }, [fieldKey]);

  useEffect(() => {
    setTargetId(null);
    setDryRunCount(null);
    if (config.needsBusiness && !selectedBusinessId) {
      setTargetOptions([]);
      return;
    }
    (async () => {
      const separator = config.targetApiPath.includes('?') ? '&' : '?';
      const url = config.needsBusiness
        ? `${config.targetApiPath}${separator}businessId=${selectedBusinessId}`
        : `${config.targetApiPath}${separator}includeArchived=false`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const items = Array.isArray(data) ? data : data.items ?? [];
      setTargetOptions(items.map((d: any) => ({ id: d.id, name: d.name })));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.targetApiPath, config.needsBusiness, selectedBusinessId]);

  const buildRequest = (dryRun: boolean) => ({
    field: fieldKey,
    targetId,
    businessId: selectedBusinessId,
    scope: scope === 'filtered' ? 'Filtered' : 'Blank',
    filters: scope === 'filtered' ? Object.fromEntries(filters.map((f) => [f.column, f.value])) : null,
    dryRun,
  });

  // Live "N rows will be filled" count, debounced — recomputed whenever the
  // field/scope/business/target selection settles.
  useEffect(() => {
    if (!targetId || (config.needsBusiness && !selectedBusinessId)) {
      setDryRunCount(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setDryRunLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/import-uploads/${batchId}/rows/auto-fill`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(buildRequest(true)),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to preview auto-fill');
        setDryRunCount(data.updatedCount);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setDryRunLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, fieldKey, scope, targetId, selectedBusinessId, config.needsBusiness]);

  const handleApply = async () => {
    if (!targetId) return;
    setApplying(true);
    setError(null);
    try {
      const res = await fetch(`/api/import-uploads/${batchId}/rows/auto-fill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(buildRequest(false)),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to apply auto-fill');
      setAppliedCount(data.updatedCount);
      setDryRunCount(null);
      setTargetId(null);
      onApplied();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setApplying(false);
    }
  };

  const selectedTarget = targetOptions.find((o) => o.id === targetId) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto Fill</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pick a field and a value, and which unmatched rows get it.
        </Typography>

        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as Scope)} sx={{ mb: 2 }}>
          <FormControlLabel
            value="filtered"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">
                  {filters.length > 0 ? 'Rows matching the current filters' : 'All unmatched rows (no filters set)'}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  Replaces whatever each row has or was suggested.
                </Typography>
                {filters.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {filters.map((f) => (
                      <Chip key={f.column} size="small" variant="outlined" label={`${f.label}: ${f.value}`} />
                    ))}
                  </Box>
                )}
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.25 } }}
          />
          <FormControlLabel
            value="blank"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">Rows with nothing for this field (whole batch)</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  Rows that already have a raw value or a pre-set are left untouched.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.25 } }}
          />
        </RadioGroup>

        <ToggleButtonGroup
          value={fieldKey}
          exclusive
          size="small"
          onChange={(_, v) => v && setFieldKey(v)}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          {FIELD_CONFIGS.map((c) => (
            <ToggleButton key={c.field} value={c.field}>{c.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        {config.needsBusiness && (
          <Autocomplete
            sx={{ mb: 2 }}
            size="small"
            options={businesses}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={businesses.find((b) => b.id === selectedBusinessId) || null}
            onChange={(_, v) => setSelectedBusinessId(v?.id || null)}
            renderInput={(params) => <TextField {...params} label="Business" helperText={`${config.label} names are scoped per Business`} />}
          />
        )}

        <Autocomplete
          size="small"
          options={targetOptions}
          disabled={config.needsBusiness && !selectedBusinessId}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={selectedTarget}
          onChange={(_, v) => setTargetId(v?.id || null)}
          renderInput={(params) => (
            <TextField {...params} label={scope === 'filtered' ? `Set ${config.label} to` : `Fill blank ${config.label} with`} />
          )}
        />

        <Box sx={{ mt: 2, minHeight: 32 }}>
          {dryRunLoading ? (
            <CircularProgress size={20} />
          ) : dryRunCount !== null ? (
            <Typography variant="body2">
              {scope === 'filtered' ? (
                <>
                  This will set {config.label} to <strong>{selectedTarget?.name}</strong> on <strong>{dryRunCount}</strong>{' '}
                  {filters.length > 0 ? 'filtered' : 'unmatched'} row(s)
                  {config.needsBusiness && ' whose Business is the one picked above'}
                  {fieldKey === 'FieldName' && ', and their Farm to this field\'s farm'}.
                </>
              ) : (
                <>This will fill <strong>{dryRunCount}</strong> row(s) currently missing {config.label}.</>
              )}
            </Typography>
          ) : null}
        </Box>

        {appliedCount !== null && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {scope === 'filtered' ? 'Set' : 'Filled'} {appliedCount} row(s) — pick another field to keep going, or close.
          </Alert>
        )}
        {error && (
          <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          disabled={!targetId || applying || dryRunCount === 0}
          onClick={handleApply}
        >
          {applying ? 'Applying...' : `${scope === 'filtered' ? 'Set' : 'Fill'} ${dryRunCount ?? ''} Row(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportAutoFillDialog;
